import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import * as pdfjsLib from 'pdfjs-dist';
import { AiOutlineLeft, AiOutlineRight, AiOutlineZoomIn, AiOutlineZoomOut } from 'react-icons/ai';
import axios from 'axios';

// Configurar PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const EmbeddedDocumentViewer = () => {
  const { documentId: documentIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const documentId = documentIdParam || searchParams.get('id');
  const operation = searchParams.get('operation') || 'view';
  const tokenFromQuery = searchParams.get('token');
  const [tokenFromMessage, setTokenFromMessage] = useState(null);

  // Escuchar mensajes del padre (para integración segura vía postMessage)
  useEffect(() => {
    console.log('EmbeddedDocumentViewer: Mounting and adding message listener');
    const handleMessage = (event) => {
      console.log('EmbeddedDocumentViewer: Message received', event.data);
      // Por seguridad, podrías validar event.origin aquí si conoces los dominios permitidos
      if (event.data && event.data.type === 'setToken' && event.data.token) {
        console.log('EmbeddedDocumentViewer: Valid setToken message received');
        setTokenFromMessage(event.data.token);
      }
    };

    window.addEventListener('message', handleMessage);

    // Notificar al padre que estamos listos para recibir el token
    if (window.parent && window.parent !== window) {
      console.log('EmbeddedDocumentViewer: Sending readyForToken signal');
      window.parent.postMessage({ type: 'readyForToken' }, '*');
    }

    return () => {
      console.log('EmbeddedDocumentViewer: Removing message listener');
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Intentar obtener el token automáticamente del AuthProvider si está disponible
  const auth = useAuth();
  const token = tokenFromQuery || tokenFromMessage || auth.user?.access_token;
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const renderTaskRef = React.useRef(null);

  // Cargar PDF - intenta usar token automáticamente o del query parameter
  useEffect(() => {
    const loadPDF = async () => {
      if (!documentId) {
        setError('ID de documento no proporcionado');
        setLoading(false);
        return;
      }

      // Esperar a que el auth termine de cargar si está disponible
      if (auth.isLoading) {
        return;
      }

      // Obtener token: primero del query parameter, luego del AuthProvider
      const finalToken = token || auth.user?.access_token;

      console.log('EmbeddedDocumentViewer: Check token', {
        hasToken: !!finalToken,
        source: tokenFromQuery ? 'query' : (tokenFromMessage ? 'message' : (auth.user?.access_token ? 'auth' : 'none'))
      });

      if (!finalToken) {
        setError('Token de autenticación no disponible. Por favor, proporciona un token en la URL (?token=...) o asegúrate de estar autenticado.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const baseURL = process.env.REACT_APP_API_URL || '';
        const url = `${baseURL}/api/v1/docs/${documentId}/stream/${operation}`;

        const response = await axios.get(url, {
          responseType: 'arraybuffer',
          headers: {
            'Authorization': `Bearer ${finalToken}`,
          },
        });

        const arrayBuffer = response.data;
        const pdfDoc = await pdfjsLib.getDocument(arrayBuffer).promise;

        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        setCurrentPage(1);
      } catch (err) {
        console.error('Error loading PDF:', err);
        if (err.response?.status === 401) {
          // El mensaje distingue de dónde salió el token, porque en dos de las tres fuentes lo
          // inyecta el host: decirle "tu sesión expiró" a alguien cuyo token vino por ?token= o
          // por postMessage sería falso, y no tendría nada que hacer al respecto.
          //
          // Acá NO se marca la sesión como vencida ni se ofrece volver al login: estas rutas
          // viven embebidas en un iframe de un sistema ajeno, y signinRedirect() navegaría ese
          // iframe a Azure AD, que lo bloquea por X-Frame-Options. Peor: restartLogin() le
          // borraría al usuario la sesión de Bóveda de la pestaña padre (#168).
          const tokenPropio = !tokenFromQuery && !tokenFromMessage;
          setError(tokenPropio
            ? 'La sesión expiró. Volvé a la aplicación y actualizá la página.'
            : 'El token provisto no es válido o ya venció. Pedile uno nuevo a la aplicación que abrió este visor.');

          // NO se avisa al padre por postMessage, aunque sería cómodo para que mande un token
          // nuevo. Un aviso que sale SOLO ante 401 —y no ante 404 ni ante el éxito— es un oráculo:
          // `nginx.conf` sirve estas rutas con `frame-ancestors *`, así que cualquier sitio puede
          // embeberlas, iterar ids de documento y deducir, por recibir o no el mensaje, cuáles
          // existen y cuáles el usuario tiene permiso de ver. Sin llegar a ver el contenido.
          //
          // Para poder avisar hace falta primero una allowlist de orígenes configurable, que hoy
          // no existe: ver #174 en el backlog, donde va junto con lo preexistente de que el
          // listener de `message` acepta `setToken` de cualquier origen.
        } else if (err.response?.status === 404) {
          setError('Documento no encontrado. Verifica el ID del documento.');
        } else {
          setError('Error al cargar el documento. Por favor, verifica que el ID y el token sean correctos.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadPDF();
  }, [documentId, operation, token, auth.user?.access_token, auth.isLoading]);

  // Renderizar página actual
  useEffect(() => {
    const renderPage = async () => {
      if (!pdf || !canvasRef.current) return;

      try {
        // Cancelar operación de renderizado anterior si existe
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const page = await pdf.getPage(currentPage);
        const canvas = canvasRef.current;

        if (!canvas) return;

        const context = canvas.getContext('2d');
        const viewport = page.getViewport({ scale });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        context.clearRect(0, 0, canvas.width, canvas.height);

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        renderTaskRef.current = null;
      } catch (err) {
        if (err.name === 'RenderingCancelledException') {
          return;
        }
        console.error('Error rendering page:', err);
        setError('Error al renderizar la página del documento.');
      }
    };

    renderPage();
  }, [pdf, currentPage, scale]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, []);

  // Navegación
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Cargando documento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h3 style={styles.errorTitle}>Error al cargar el documento</h3>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header con controles */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.pageInfo}>
            Página {currentPage} de {totalPages}
          </span>
        </div>

        <div style={styles.headerRight}>
          <div style={styles.controls}>
            <button
              className="embedded-control-button"
              style={styles.controlButton}
              onClick={zoomOut}
              title="Alejar"
            >
              <AiOutlineZoomOut />
            </button>
            <span style={styles.zoomText}>{Math.round(scale * 100)}%</span>
            <button
              className="embedded-control-button"
              style={styles.controlButton}
              onClick={zoomIn}
              title="Acercar"
            >
              <AiOutlineZoomIn />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido del PDF */}
      <div style={styles.content} ref={containerRef}>
        <div style={styles.pdfContainer}>
          <canvas
            ref={canvasRef}
            style={styles.canvas}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          />
        </div>
      </div>

      {/* Footer con navegación */}
      <div style={styles.footer}>
        <div style={styles.navigation}>
          <button
            className="embedded-nav-button"
            style={{
              ...styles.navButton,
              ...(currentPage <= 1 ? styles.navButtonDisabled : {})
            }}
            onClick={goToPreviousPage}
            disabled={currentPage <= 1}
            title="Página anterior"
          >
            <AiOutlineLeft />
          </button>

          <span style={styles.pageIndicator}>
            {currentPage} / {totalPages}
          </span>

          <button
            className="embedded-nav-button"
            style={{
              ...styles.navButton,
              ...(currentPage >= totalPages ? styles.navButtonDisabled : {})
            }}
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            title="Página siguiente"
          >
            <AiOutlineRight />
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    backgroundColor: '#F9FAFB',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E5E7EB',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageInfo: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: '500',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  controlButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#374151',
    fontSize: '16px',
    transition: 'all 0.2s ease',
  },
  controlButtonHover: {
    backgroundColor: '#E5E7EB',
    borderColor: '#D1D5DB',
  },
  zoomText: {
    fontSize: '12px',
    color: '#6B7280',
    minWidth: '40px',
    textAlign: 'center',
    fontWeight: '500',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#F9FAFB',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '20px',
  },
  pdfContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  canvas: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #E5E7EB',
    boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.1)',
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    backgroundColor: '#3B82F6',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#FFFFFF',
    fontSize: '16px',
    transition: 'all 0.2s ease',
  },
  navButtonHover: {
    backgroundColor: '#2563EB',
  },
  navButtonDisabled: {
    backgroundColor: '#9CA3AF',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  pageIndicator: {
    fontSize: '14px',
    color: '#374151',
    minWidth: '80px',
    textAlign: 'center',
    fontWeight: '500',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #F3F4F6',
    borderTop: '4px solid #3B82F6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6B7280',
    margin: 0,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px',
    textAlign: 'center',
    padding: '20px',
  },
  errorTitle: {
    fontSize: '18px',
    color: '#DC2626',
    margin: 0,
    fontWeight: '600',
  },
  errorText: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
  },
};

// Agregar animación CSS para el spinner si no existe
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('embedded-viewer-spinner-style');
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = 'embedded-viewer-spinner-style';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default EmbeddedDocumentViewer;

